// Angular
import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';

// RxJS
import { catchError, switchMap, tap, throwError } from 'rxjs';

// Third-party libraries
import { ToastService } from '@sms-ir/ui-components';

// App types
import { ApiResponse } from '@app/core/api';
import { DEFAULT_ERROR_MESSAGE, HTTP_ERROR_MESSAGES, HttpErrorStatus } from '@app/core/types/enums/http-error.enum';

// App services
import { TokenRefreshService, TokenService } from '@app/core/services';

// App environment
import { environment } from '@env/environment';


const apiUrl = environment.apiUrl?.replace(/\/$/, '') ?? '';
const isRefreshRequest = (url: string) => url.includes('/api/app/auth/token');

export const apiResponseInterceptor: HttpInterceptorFn = (req, next) => {
  if (!apiUrl || !req.url.startsWith(apiUrl)) {
    return next(req);
  }

  const toast = inject(ToastService);
  const tokenRefresh = inject(TokenRefreshService);
  const tokenService = inject(TokenService);

  return next(req).pipe(
    tap(event => {
      if (!(event instanceof HttpResponse)) {
        return;
      }

      const body = event.body as ApiResponse<null, number, string>;

      if (body?.success) {
        return;
      }

      toast.add({
        variant: 'error',
        message: body.description?.trim() || DEFAULT_ERROR_MESSAGE,
        icon: true,
        dismissible: true,
        duration: 4000,
      });
    }),
    catchError((error: HttpErrorResponse) => {
      if (error.status === HttpErrorStatus.Unauthorized && !isRefreshRequest(req.url) && tokenService.hasRefreshToken()) {
        return tokenRefresh.refresh().pipe(
          switchMap(accessToken => next(req.clone({
            setHeaders: { Authorization: `Bearer ${accessToken}` },
          }))),
        );
      }

      const message = HTTP_ERROR_MESSAGES[error.status as HttpErrorStatus] ?? DEFAULT_ERROR_MESSAGE;

      toast.add({
        variant: 'error',
        message,
        icon: true,
        dismissible: true,
        duration: 4000,
      });

      return throwError(() => error);
    }),
  );
};
